/* eslint-disable max-len */
import { ErrorMessage, useField, useFormikContext } from 'formik';
import {
  ChangeEvent,
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import FormCheckbox from '../../../../components/FormInputs/FormCheckbox/FormCheckbox';
import FormDateSelector from '../../../../components/FormInputs/FormDateSelector/FormDateSelector';
import FormDropDown from '../../../../components/FormInputs/FormDropdown/FormDropdown';
import FormInputLabel from '../../../../components/FormInputs/FormInputLabel/FormInputLabel';
import FormTextarea from '../../../../components/FormInputs/FormTextarea/FormTextarea';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import InlineError from '../../../../components/FormInputs/InlineError/InlineError';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import Panel from '../../../../components/Panel/Panel';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  clearDyslexiaScreenerDetail,
  saveDyslexiaScreener,
  selectDyslexiaScreenerDataEntryMode,
  selectDyslexiaScreenerDetail,
  selectDyslexiaScreenerFiles,
  selectDyslexiaScreenerFormViewModel,
  selectDyslexiaScreenerListUpdateAllowed,
  selectDyslexiaScreenerLoadingDetail,
  selectDyslexiaScreenerLoadingFormViewModel,
  selectDyslexiaScreenerPendingListRowContext,
  setDyslexiaScreenerDataEntryMode,
  setDyslexiaScreenerPanelNewStaffTrainingRequired,
} from '../../../../redux/slices/dyslexiaScreenerSlice';
import {
  fetchDyslexiaScreenerById,
  fetchDyslexiaScreenerFilesBySchoolThunk,
  fetchDyslexiaScreenerFormViewModel,
} from '../../../../redux/thunks/dyslexiaScreenerThunks';
import {
  createDyslexiaScreener,
  updateDyslexiaScreener,
} from '../../../../services/dyslexiaScreener.service';
import { appRoutePaths } from '../../../../services/route.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { DyslexiaScreenerFormViewModel } from '../../../../types/DyslexiaScreenerFormViewModel';
import { DyslexiaScreenerModel } from '../../../../types/DyslexiaScreenerModel';
import { Tabs_DISTRICT_SCHOOL } from '../../../../types/NavigationTabs';
import { PanelType } from '../../../../types/PanelType';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import {
  buildDyslexiaScreenerValidationSchemaWithDocumentUploads,
  DyslexiaScreenerValidationSchema,
} from '../../../../types/Validation/DyslexiaScreenerValidationSchema';
import {
  buildDyslexiaScreenerListRoute,
  deriveDyslexiaScreenerNewStaffTrainingRequired,
  dyslexiaScreenerNewStaffTrainingUiRef,
  DYSLEXIA_SCREENER_ANNUAL_ACKNOWLEDGEMENT_TEXT,
  DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO,
  DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES,
  DYSLEXIA_SCREENER_TRAINING_SOURCE_OPTIONS,
  DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE,
  formatDyslexiaScreenerFailedOrAtRiskPercentage,
  formatDyslexiaScreenerTrainingCycleDisplay,
  isTrainingAcknowledgementDatePopulated,
  parseDyslexiaScreenerListFiltersFromSearch,
  shouldShowDyslexiaScreenerNewStaffTrainingRadio,
  shouldShowDyslexiaScreenerTrainingDetailFields,
} from '../../../../utilities/dyslexiaScreenerUtilities';
import { applyClassesForInput } from '../../../../utilities/formUtilities';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import './DyslexiaScreenerInput.css';
import DyslexiaScreenerUploadDocuments from './DyslexiaScreenerUploadDocuments';

/** When false (from list API `dyslexiaScreenerUpdate`), all editable dyslexia input fields are read-only. */
const DyslexiaScreenerFormFieldsLockedContext = createContext(false);

const useDyslexiaScreenerFormFieldsLocked = (): boolean =>
  useContext(DyslexiaScreenerFormFieldsLockedContext);

const formatDisplayNameWithIdentifier = (
  name: string,
  identifier: string
): string => {
  const n = (name ?? '').trim();
  const i = (identifier ?? '').trim();
  if (n && i) {
    return `${n} (${i})`;
  }
  return n || i;
};

const DyslexiaScreenerReadOnlyLeaSchoolRow = (): ReactElement => {
  const pending = useAppSelector(selectDyslexiaScreenerPendingListRowContext);
  const { values } = useFormikContext<DyslexiaScreenerModel>();
  const leaDisplay = formatDisplayNameWithIdentifier(
    values.districtName || pending?.districtName || '',
    values.leaIdentifier || pending?.leaIdentifier || ''
  );
  const schoolDisplay = formatDisplayNameWithIdentifier(
    values.schoolName || pending?.schoolName || '',
    values.schoolIdentifier || pending?.schoolIdentifier || ''
  );

  return (
    <div className="field-row">
      <div
        className="field-container disabled"
        data-testid="form-readonly-lea-name-combined"
      >
        <FormInputLabel
          field="districtNameCombinedDisplay"
          displayName="LEA Name"
          disabled={true}
        />
        <input
          id="districtNameCombinedDisplay"
          className={applyClassesForInput('districtName', ' data-input')}
          type="text"
          disabled
          readOnly
          value={leaDisplay}
        />
      </div>
      <div
        className="field-container disabled"
        data-testid="form-readonly-school-name-combined"
      >
        <FormInputLabel
          field="schoolNameCombinedDisplay"
          displayName="School Name"
          disabled={true}
        />
        <input
          id="schoolNameCombinedDisplay"
          className={applyClassesForInput('schoolName', ' data-input')}
          type="text"
          disabled
          readOnly
          value={schoolDisplay}
        />
      </div>
    </div>
  );
};

const DyslexiaScreenerReadOnlyAggregatedCounts = (): ReactElement => {
  const pending = useAppSelector(selectDyslexiaScreenerPendingListRowContext);
  const detail = useAppSelector(selectDyslexiaScreenerDetail);
  const { values } = useFormikContext<DyslexiaScreenerModel>();
  const toFinite = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const kgIep = toFinite(
    pending?.kgIEPStudentCount ??
      detail.kgIEPStudentCount ??
      values.kgIEPStudentCount
  );
  const g1Iep = toFinite(
    pending?.grade1IEPStudentCount ??
      detail.grade1IEPStudentCount ??
      values.grade1IEPStudentCount
  );
  const kg504 = toFinite(
    pending?.kg504StudentCount ??
      detail.kg504StudentCount ??
      values.kg504StudentCount
  );
  const g1504 = toFinite(
    pending?.grade1504StudentCount ??
      detail.grade1504StudentCount ??
      values.grade1504StudentCount
  );
  const iepTotal = kgIep + g1Iep;
  const plan504Total = kg504 + g1504;

  return (
    <div className="field-row">
      <div
        className="field-container disabled"
        data-testid="form-readonly-iep-student-total"
      >
        <FormInputLabel
          field="iepStudentCountTotalDisplay"
          displayName="IEP Student Count"
          disabled={true}
        />
        <input
          id="iepStudentCountTotalDisplay"
          className={applyClassesForInput(
            'iepStudentCountTotalDisplay',
            ' data-input'
          )}
          type="text"
          disabled
          readOnly
          value={String(iepTotal)}
        />
      </div>
      <div
        className="field-container disabled"
        data-testid="form-readonly-504-plan-total"
      >
        <FormInputLabel
          field="section504PlanCountTotalDisplay"
          displayName="Section 504 Plan Count"
          disabled={true}
        />
        <input
          id="section504PlanCountTotalDisplay"
          className={applyClassesForInput(
            'section504PlanCountTotalDisplay',
            ' data-input'
          )}
          type="text"
          disabled
          readOnly
          value={String(plan504Total)}
        />
      </div>
    </div>
  );
};

const DyslexiaScreenerReadOnlySummary = (): ReactElement => (
  <>
    <DyslexiaScreenerReadOnlyLeaSchoolRow />
    <DyslexiaScreenerReadOnlyAggregatedCounts />
  </>
);

const useGrade1CompletionDatePopulated = (): boolean => {
  const { values } = useFormikContext<DyslexiaScreenerModel>();
  return Boolean(
    values.grade1ScreenerCompletionDate &&
      values.grade1ScreenerCompletionDate.trim().length > 0
  );
};

const useKgCompletionDatePopulated = (): boolean => {
  const { values } = useFormikContext<DyslexiaScreenerModel>();
  return Boolean(
    values.kgScreenerCompletionDate &&
      values.kgScreenerCompletionDate.trim().length > 0
  );
};

const Grade1ScreenerUsedDropdown = ({
  options,
}: {
  options: DyslexiaScreenerFormViewModel['dyslexiaScreenerAssementOptions'];
}): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const isCompletionDatePopulated = useGrade1CompletionDatePopulated();
  return (
    <FormDropDown
      displayName="Screener Used"
      field="grade1ScreenerUsed"
      options={options || []}
      isClearable={true}
      disabled={formFieldsLocked || !isCompletionDatePopulated}
      validationSchema={DyslexiaScreenerValidationSchema}
      classConfig={{
        labelClasses: isCompletionDatePopulated ? 'forced-required-label' : '',
      }}
    />
  );
};

const Grade1ParentsNotifiedCheckbox = (): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const isCompletionDatePopulated = useGrade1CompletionDatePopulated();
  return (
    <div className="dyslexia-screener-checkbox-cell">
      <FormCheckbox
        displayName="Parents Notified"
        field="grade1ParentsNotified"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{
          labelClasses: isCompletionDatePopulated
            ? 'forced-required-label'
            : '',
        }}
      />
      <ErrorMessage name="grade1ParentsNotified" component={InlineError} />
    </div>
  );
};

const Grade1ScreenerCountFields = (): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { values, setFieldValue } = useFormikContext<DyslexiaScreenerModel>();
  const isCompletionDatePopulated = Boolean(
    values.grade1ScreenerCompletionDate &&
      values.grade1ScreenerCompletionDate.trim().length > 0
  );
  const requiredLabelClass = isCompletionDatePopulated
    ? 'forced-required-label'
    : '';

  useEffect(() => {
    setFieldValue(
      'grade1FailedOrAtRiskPercentage',
      formatDyslexiaScreenerFailedOrAtRiskPercentage(
        values.grade1ScreenerCount,
        values.grade1ScreenerFailedOrAtRiskCount
      ),
      false
    );
  }, [
    setFieldValue,
    values.grade1ScreenerCount,
    values.grade1ScreenerFailedOrAtRiskCount,
  ]);

  return (
    <div className="field-row">
      <FormTextbox
        displayName="Screener Count"
        field="grade1ScreenerCount"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
      <FormTextbox
        displayName="Failed or At Risk Count"
        field="grade1ScreenerFailedOrAtRiskCount"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
      <FormTextbox
        displayName="Percentage Failed or At Risk"
        field="grade1FailedOrAtRiskPercentage"
        disabled={true}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
    </div>
  );
};

const KgScreenerUsedDropdown = ({
  options,
}: {
  options: DyslexiaScreenerFormViewModel['dyslexiaScreenerAssementOptions'];
}): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const isCompletionDatePopulated = useKgCompletionDatePopulated();
  return (
    <FormDropDown
      displayName="Screener Used"
      field="kgScreenerUsed"
      options={options || []}
      isClearable={true}
      disabled={formFieldsLocked || !isCompletionDatePopulated}
      validationSchema={DyslexiaScreenerValidationSchema}
      classConfig={{
        labelClasses: isCompletionDatePopulated ? 'forced-required-label' : '',
      }}
    />
  );
};

const KgParentsNotifiedCheckbox = (): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const isCompletionDatePopulated = useKgCompletionDatePopulated();
  return (
    <div className="dyslexia-screener-checkbox-cell">
      <FormCheckbox
        displayName="Parents Notified"
        field="kgParentsNotified"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{
          labelClasses: isCompletionDatePopulated
            ? 'forced-required-label'
            : '',
        }}
      />
      <ErrorMessage name="kgParentsNotified" component={InlineError} />
    </div>
  );
};

const KgScreenerCountFields = (): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { values, setFieldValue } = useFormikContext<DyslexiaScreenerModel>();
  const isCompletionDatePopulated = Boolean(
    values.kgScreenerCompletionDate &&
      values.kgScreenerCompletionDate.trim().length > 0
  );
  const requiredLabelClass = isCompletionDatePopulated
    ? 'forced-required-label'
    : '';

  useEffect(() => {
    setFieldValue(
      'kgFailedOrAtRiskPercentage',
      formatDyslexiaScreenerFailedOrAtRiskPercentage(
        values.kgScreenerCount,
        values.kgScreenerFailedOrAtRiskCount
      ),
      false
    );
  }, [
    setFieldValue,
    values.kgScreenerCount,
    values.kgScreenerFailedOrAtRiskCount,
  ]);

  return (
    <div className="field-row">
      <FormTextbox
        displayName="Screener Count"
        field="kgScreenerCount"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
      <FormTextbox
        displayName="Failed or At Risk Count"
        field="kgScreenerFailedOrAtRiskCount"
        disabled={formFieldsLocked || !isCompletionDatePopulated}
        validationSchema={DyslexiaScreenerValidationSchema}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
      <FormTextbox
        displayName="Percentage Failed or At Risk"
        field="kgFailedOrAtRiskPercentage"
        disabled={true}
        classConfig={{ labelClasses: requiredLabelClass }}
      />
    </div>
  );
};

const AwarenessTrainingCycleFields = ({
  schoolYearMinDate,
  schoolYearMaxDate,
}: {
  schoolYearMinDate: Date;
  schoolYearMaxDate: Date;
}): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { values } = useFormikContext<DyslexiaScreenerModel>();
  const schoolYearDisplay =
    values.schoolYear > 0
      ? `${values.schoolYear - 1}-${values.schoolYear}`
      : '';
  const trainingCycleDisplay = formatDyslexiaScreenerTrainingCycleDisplay(
    values.schoolYear
  );

  return (
    <div className="field-row">
      <div className="field-container disabled">
        <FormInputLabel
          field="trainingCycle"
          displayName="Training Cycle"
          disabled={true}
        />
        <input
          id="trainingCycleDisplay"
          className={applyClassesForInput('trainingCycle', ' data-input')}
          type="text"
          disabled
          readOnly
          value={trainingCycleDisplay}
        />
      </div>
      <div className="field-container disabled">
        <FormInputLabel
          field="schoolYear"
          displayName="School Year"
          disabled={true}
        />
        <input
          id="schoolYearDisplay"
          className={applyClassesForInput('schoolYear', ' data-input')}
          type="text"
          disabled
          readOnly
          value={schoolYearDisplay}
        />
      </div>
      <FormDateSelector
        displayName="Training Acknowledgement Date for Selected School Year"
        field="trainingAcknowledgementDate"
        validationSchema={DyslexiaScreenerValidationSchema}
        minDate={schoolYearMinDate}
        maxDate={schoolYearMaxDate}
        isClearable
        disabled={formFieldsLocked}
      />
    </div>
  );
};

const clearDyslexiaScreenerTrainingFollowUpFields = (
  setFieldValue: (
    field: keyof DyslexiaScreenerModel,
    value: unknown,
    shouldValidate?: boolean
  ) => void,
  setNewStaffTrainingRequired: (value: string) => void
): void => {
  setNewStaffTrainingRequired('');
  dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired = '';
  setFieldValue('trainingUsed', '', false);
  setFieldValue('otherTrainingUsed', '', false);
  setFieldValue('otherTrainingAcknowldegment', false, false);
};

const DyslexiaScreenerTrainingFollowUpFields = (): ReactElement | null => {
  const dispatch = useAppDispatch();
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { values, setFieldValue, validateForm } =
    useFormikContext<DyslexiaScreenerModel>();
  /** Same field as FormDateSelector; used for invalid styling without conditional hooks. */
  const [, trainingAckFieldMeta] = useField('trainingAcknowledgementDate');
  const newStaffRadioListClassName =
    trainingAckFieldMeta.touched && trainingAckFieldMeta.error
      ? 'is-invalid'
      : '';
  const trainingAckDate = values.trainingAcknowledgementDate ?? '';
  const hasTrainingAckDate =
    isTrainingAcknowledgementDatePopulated(trainingAckDate);
  const [newStaffTrainingRequired, setNewStaffTrainingRequired] = useState(
    () => {
      const initial = deriveDyslexiaScreenerNewStaffTrainingRequired(
        values.schoolYear,
        trainingAckDate,
        values
      );
      dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired = initial;
      return initial;
    }
  );
  const showNewStaffRadio = shouldShowDyslexiaScreenerNewStaffTrainingRadio(
    values.schoolYear,
    trainingAckDate
  );
  const showTrainingDetails = shouldShowDyslexiaScreenerTrainingDetailFields(
    values.schoolYear,
    trainingAckDate,
    newStaffTrainingRequired
  );
  const requiredLabelClass = showTrainingDetails ? 'forced-required-label' : '';
  const acknowledgeTrainingRequiredClass = hasTrainingAckDate
    ? 'forced-required-label'
    : '';
  const trainingSourceIsOther =
    values.trainingUsed?.trim() ===
    DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE;

  useEffect(() => {
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired =
      newStaffTrainingRequired;
    dispatch(
      setDyslexiaScreenerPanelNewStaffTrainingRequired(newStaffTrainingRequired)
    );
  }, [dispatch, newStaffTrainingRequired]);

  useEffect(() => {
    void validateForm();
  }, [newStaffTrainingRequired, validateForm]);

  useEffect(() => {
    if (!hasTrainingAckDate) {
      clearDyslexiaScreenerTrainingFollowUpFields(
        setFieldValue,
        setNewStaffTrainingRequired
      );
      return;
    }
    const derived = deriveDyslexiaScreenerNewStaffTrainingRequired(
      values.schoolYear,
      trainingAckDate,
      values
    );
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired = derived;
    setNewStaffTrainingRequired(derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasTrainingAckDate,
    setFieldValue,
    values.id,
    values.schoolYear,
    trainingAckDate,
  ]);

  useEffect(() => {
    if (
      showNewStaffRadio &&
      newStaffTrainingRequired === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO
    ) {
      setFieldValue('trainingUsed', '', false);
      setFieldValue('otherTrainingUsed', '', false);
    }
  }, [newStaffTrainingRequired, showNewStaffRadio, setFieldValue]);

  useEffect(() => {
    if (!showTrainingDetails) {
      return;
    }
    if (trainingSourceIsOther) {
      return;
    }
    if ((values.otherTrainingUsed ?? '').trim() !== '') {
      setFieldValue('otherTrainingUsed', '', false);
    }
  }, [
    showTrainingDetails,
    trainingSourceIsOther,
    values.otherTrainingUsed,
    setFieldValue,
  ]);

  if (!hasTrainingAckDate) {
    return null;
  }

  const handleNewStaffTrainingChange = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    const selection = event.target.value;
    dyslexiaScreenerNewStaffTrainingUiRef.newStaffTrainingRequired = selection;
    dispatch(setDyslexiaScreenerPanelNewStaffTrainingRequired(selection));
    setNewStaffTrainingRequired(selection);
    if (selection === DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO) {
      setFieldValue('trainingUsed', '', false);
      setFieldValue('otherTrainingUsed', '', false);
    }
  };

  return (
    <>
      {showNewStaffRadio ? (
        <div className="field-row">
          <div
            className="field-container field-radio-list"
            data-testid="form-radio-list-newStaffTrainingRequired"
          >
            <FormInputLabel
              field="trainingAcknowledgementDate"
              displayName="For the selected school year, were there new staff who were required to complete this training?"
              validationSchema={DyslexiaScreenerValidationSchema}
              classes="forced-required-label"
            />
            <div
              data-testid="radio-list-container"
              className={`radio-list-container horizontal-layout ${newStaffRadioListClassName}`}
            >
              {[
                {
                  text: DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES,
                  value: DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_YES,
                },
                {
                  text: DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO,
                  value: DYSLEXIA_SCREENER_NEW_STAFF_TRAINING_NO,
                },
              ].map((opt) => (
                <div className="radio-list-option" key={opt.value}>
                  <input
                    type="radio"
                    id={`newStaffTrainingRequired_${opt.value}`}
                    name="newStaffTrainingRequired"
                    value={opt.value}
                    data-testid={`rdo-${opt.value}`}
                    checked={newStaffTrainingRequired === opt.value}
                    onChange={handleNewStaffTrainingChange}
                    disabled={formFieldsLocked}
                  />
                  <label htmlFor={`newStaffTrainingRequired_${opt.value}`}>
                    {opt.text}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {showTrainingDetails ? (
        <div className="field-row field-row--dyslexia-screener-training-sources">
          <FormDropDown
            displayName="Training Sources Used"
            field="trainingUsed"
            options={DYSLEXIA_SCREENER_TRAINING_SOURCE_OPTIONS}
            isClearable={true}
            disabled={formFieldsLocked}
            validationSchema={DyslexiaScreenerValidationSchema}
            classConfig={{ labelClasses: requiredLabelClass }}
            onChangeEvent={async ({ value, setValueHook }): Promise<void> => {
              const v =
                value === undefined || value === null
                  ? ''
                  : String(value).trim();
              if (v !== DYSLEXIA_SCREENER_TRAINING_SOURCE_OTHER_VALUE) {
                setValueHook?.('otherTrainingUsed', '', false);
              }
            }}
          />
          {trainingSourceIsOther ? (
            <FormTextarea
              displayName="Other Training Sources Used"
              field="otherTrainingUsed"
              rows={4}
              disabled={formFieldsLocked}
              validationSchema={DyslexiaScreenerValidationSchema}
              classConfig={{ labelClasses: 'forced-required-label' }}
            />
          ) : null}
        </div>
      ) : null}
      <div className="field-row dyslexia-screener-training-ack-row">
        <div className="dyslexia-screener-checkbox-cell">
          <FormCheckbox
            displayName={DYSLEXIA_SCREENER_ANNUAL_ACKNOWLEDGEMENT_TEXT}
            field="otherTrainingAcknowldegment"
            disabled={formFieldsLocked}
            validationSchema={DyslexiaScreenerValidationSchema}
            classConfig={{ labelClasses: acknowledgeTrainingRequiredClass }}
          />
          <ErrorMessage
            name="otherTrainingAcknowldegment"
            component={InlineError}
          />
        </div>
      </div>
    </>
  );
};

const parseRouteSchoolYear = (sy?: string): number => {
  const matches = sy?.match(/\d{4}/g);
  if (matches && matches.length > 0) {
    return parseInt(matches[0], 10);
  }
  const n = parseInt(sy ?? '', 10);
  return Number.isNaN(n) ? 0 : n;
};

const clearGrade1ScreenerPanelFields = (
  setFieldValue: (
    field: keyof DyslexiaScreenerModel,
    value: unknown,
    shouldValidate?: boolean
  ) => void
): void => {
  setFieldValue('grade1ScreenerUsed', '', false);
  setFieldValue('grade1ScreenerCount', 0, false);
  setFieldValue('grade1ScreenerFailedOrAtRiskCount', 0, false);
  setFieldValue('grade1ParentsNotified', false, false);
  setFieldValue(
    'grade1FailedOrAtRiskPercentage',
    formatDyslexiaScreenerFailedOrAtRiskPercentage(0, 0),
    false
  );
};

const clearKgScreenerPanelFields = (
  setFieldValue: (
    field: keyof DyslexiaScreenerModel,
    value: unknown,
    shouldValidate?: boolean
  ) => void
): void => {
  setFieldValue('kgScreenerUsed', '', false);
  setFieldValue('kgScreenerCount', 0, false);
  setFieldValue('kgScreenerFailedOrAtRiskCount', 0, false);
  setFieldValue('kgParentsNotified', false, false);
  setFieldValue(
    'kgFailedOrAtRiskPercentage',
    formatDyslexiaScreenerFailedOrAtRiskPercentage(0, 0),
    false
  );
};

/** Must render under Formik so completion-date clears can reset the rest of the panel via onFormValueChange. */
const Grade1ScreenerCompletionDateSelector = ({
  minDate,
  maxDate,
}: {
  minDate: Date;
  maxDate: Date;
}): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { setFieldValue } = useFormikContext<DyslexiaScreenerModel>();
  return (
    <FormDateSelector
      displayName="Screener Completion Date"
      field="grade1ScreenerCompletionDate"
      validationSchema={DyslexiaScreenerValidationSchema}
      minDate={minDate}
      maxDate={maxDate}
      isClearable
      disabled={formFieldsLocked}
      onFormValueChange={(next) => {
        if (next.trim().length > 0) {
          return;
        }
        clearGrade1ScreenerPanelFields(setFieldValue);
      }}
    />
  );
};

const KgScreenerCompletionDateSelector = ({
  minDate,
  maxDate,
}: {
  minDate: Date;
  maxDate: Date;
}): ReactElement => {
  const formFieldsLocked = useDyslexiaScreenerFormFieldsLocked();
  const { setFieldValue } = useFormikContext<DyslexiaScreenerModel>();
  return (
    <FormDateSelector
      displayName="Screener Completion Date"
      field="kgScreenerCompletionDate"
      validationSchema={DyslexiaScreenerValidationSchema}
      minDate={minDate}
      maxDate={maxDate}
      isClearable
      disabled={formFieldsLocked}
      onFormValueChange={(next) => {
        if (next.trim().length > 0) {
          return;
        }
        clearKgScreenerPanelFields(setFieldValue);
      }}
    />
  );
};

const DyslexiaScreenerInput = (): ReactElement => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { schoolYear: urlSchoolYear } = useParams<{ schoolYear: string }>();
  const model: DyslexiaScreenerModel = useAppSelector(
    selectDyslexiaScreenerDetail
  );
  const pending = useAppSelector(selectDyslexiaScreenerPendingListRowContext);
  const showGrade1Panel =
    (pending?.grade1Exists ?? model.grade1Exists) !== false;
  const showKgPanel = (pending?.kgExists ?? model.kgExists) !== false;
  const formViewModel: DyslexiaScreenerFormViewModel = useAppSelector(
    selectDyslexiaScreenerFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectDyslexiaScreenerLoadingFormViewModel
  );
  const isLoadingModel = useAppSelector(selectDyslexiaScreenerLoadingDetail);
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectDyslexiaScreenerDataEntryMode
  );
  const dyslexiaScreenerUpdateAllowed = useAppSelector(
    selectDyslexiaScreenerListUpdateAllowed
  );
  const formFieldsLocked = !dyslexiaScreenerUpdateAllowed;
  const isLoading = isLoadingModel || isLoadingFormViewModel;
  const dyslexiaScreenerFiles = useAppSelector(selectDyslexiaScreenerFiles);
  const dyslexiaScreenerValidationSchemaWithDocuments = useMemo(
    () =>
      buildDyslexiaScreenerValidationSchemaWithDocumentUploads({
        uploadedFiles: dyslexiaScreenerFiles,
      }),
    [dyslexiaScreenerFiles]
  );

  const getSchoolYear = (): number => {
    if (dataEntryMode === DataEntryMode.EDIT) {
      return model.schoolYear;
    }
    if (urlSchoolYear && urlSchoolYear !== ':schoolYear') {
      return parseRouteSchoolYear(urlSchoolYear);
    }
    return model.schoolYear;
  };

  const intitalValues: DyslexiaScreenerModel = {
    ...model,
    schoolYear: getSchoolYear(),
  };

  const listRoute = buildDyslexiaScreenerListRoute(
    urlSchoolYear ?? '',
    parseDyslexiaScreenerListFiltersFromSearch(location.search)
  );
  const selectedSchoolYear = getSchoolYear();
  const fileApiSchoolYear =
    selectedSchoolYear > 0 ? String(selectedSchoolYear) : '';
  const schoolYearMinDate = new Date(selectedSchoolYear - 1, 6, 1);
  const schoolYearMaxDate = new Date(selectedSchoolYear, 5, 30);

  useEffect(() => {
    const recordId = (model.id ?? '').trim();
    const lea = (model.leaIdentifier || pending?.leaIdentifier || '').trim();
    const school = (
      model.schoolIdentifier ||
      pending?.schoolIdentifier ||
      ''
    ).trim();
    if (!recordId || !fileApiSchoolYear || !lea || !school || isLoading) {
      return;
    }
    void dispatch(
      fetchDyslexiaScreenerFilesBySchoolThunk({
        schoolYear: fileApiSchoolYear,
        leaId: lea,
        schoolIdentifier: school,
        showLoading: true,
      })
    );
  }, [
    dispatch,
    fileApiSchoolYear,
    isLoading,
    model.id,
    model.leaIdentifier,
    model.schoolIdentifier,
    pending?.leaIdentifier,
    pending?.schoolIdentifier,
  ]);

  return (
    <ManualInputPage<DyslexiaScreenerModel, DyslexiaScreenerFormViewModel>
      actions={ManualInputCrudAction.CREATE_AND_EDIT}
      loadingOptions={{
        isLoading: isLoading,
        loadingDataId: 'dyslexia-screener-input-loader',
      }}
      navigationTabs={buildUserAccessNavigationTabItems(Tabs_DISTRICT_SCHOOL)}
      classes={{
        pageClass: 'data-input--district-school-info--dyslexia-screener--input',
      }}
      parameterOptions={{
        routeParameterName: 'dyslexiaScreenerId',
        identityName: model.id,
      }}
      routes={{
        backBarRoute: listRoute,
        cancelRoute: listRoute,
        createSuccessRoute: () =>
          appRoutePaths.DyslexiaScreenerInput('new', urlSchoolYear ?? ''),
      }}
      dataFunctions={{
        loadDataDetailById: fetchDyslexiaScreenerById,
        loadFormDetail: fetchDyslexiaScreenerFormViewModel,
        setEntryMode: setDyslexiaScreenerDataEntryMode,
        setData: saveDyslexiaScreener,
        clearData: clearDyslexiaScreenerDetail,
        onCreate: createDyslexiaScreener,
        onUpdate: updateDyslexiaScreener,
      }}
      formDetails={{
        validationSchema: dyslexiaScreenerValidationSchemaWithDocuments,
        initialFormValues: intitalValues,
        isFormModelEmpty: true,
      }}
      config={{
        dataEntryMode: dataEntryMode,
        objectVerbiage: 'dyslexia screener',
        pageHeader: 'LEA & School Info',
      }}
      DEV_MODE={GLOBAL_DEBUG_MODE}
      REDIRECT_TO_LIST_PAGE={true}
    >
      <DyslexiaScreenerFormFieldsLockedContext.Provider
        value={formFieldsLocked}
      >
        <div data-testid="dyslexia-screener-input-child">
          <Panel panelType={PanelType.INFO} heading="LEA & School Information">
            <DyslexiaScreenerReadOnlySummary />
          </Panel>
          {showGrade1Panel ? (
            <Panel
              panelType={PanelType.INFO}
              heading="First Grade Dyslexia Screening"
            >
              <div className="field-row">
                <Grade1ScreenerCompletionDateSelector
                  minDate={schoolYearMinDate}
                  maxDate={schoolYearMaxDate}
                />
                <Grade1ScreenerUsedDropdown
                  options={formViewModel.dyslexiaScreenerAssementOptions}
                />
                <Grade1ParentsNotifiedCheckbox />
              </div>
              <Grade1ScreenerCountFields />
            </Panel>
          ) : null}
          {showKgPanel ? (
            <Panel
              panelType={PanelType.INFO}
              heading="Kindergarten Dyslexia Screening"
            >
              <div className="field-row">
                <KgScreenerCompletionDateSelector
                  minDate={schoolYearMinDate}
                  maxDate={schoolYearMaxDate}
                />
                <KgScreenerUsedDropdown
                  options={formViewModel.dyslexiaScreenerAssementOptions}
                />
                <KgParentsNotifiedCheckbox />
              </div>
              <KgScreenerCountFields />
            </Panel>
          ) : null}
          <Panel panelType={PanelType.INFO} heading="Awareness Training Cycle">
            <AwarenessTrainingCycleFields
              schoolYearMinDate={schoolYearMinDate}
              schoolYearMaxDate={schoolYearMaxDate}
            />
            <DyslexiaScreenerTrainingFollowUpFields />
          </Panel>
          <br />
          <DyslexiaScreenerUploadDocuments
            schoolYear={fileApiSchoolYear}
            leaIdentifier={model.leaIdentifier || pending?.leaIdentifier || ''}
            schoolIdentifier={
              model.schoolIdentifier || pending?.schoolIdentifier || ''
            }
            dyslexiaScreenerId={model.id}
            disabled={formFieldsLocked}
          />
        </div>
      </DyslexiaScreenerFormFieldsLockedContext.Provider>
    </ManualInputPage>
  );
};

export default DyslexiaScreenerInput;
